// babel: translations come from the bridge service (babel/v1/translation); this file also manages
// the user's language settings, stored on the Tinode account as public.babel = {lang, also}.

const LANG_KEY = 'babel_lang';

// Languages the translation service supports (BCP-47 code -> display name).
export const LANGUAGES = {
  en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch', pt: 'Português', it: 'Italiano',
  ru: 'Русский', zh: '中文', ja: '日本語', ko: '한국어', ar: 'العربية', he: 'עברית', hi: 'हिन्दी'
};

function meBabel(tinode) {
  try {
    const me = tinode && tinode.getMeTopic();
    return (me && me.public && me.public.babel) || null;
  } catch (_) {
    return null;
  }
}

// Reader's primary language: account setting, else last known value, else the browser language.
export function getMyLang(tinode) {
  const b = meBabel(tinode);
  try {
    if (b && b.lang) {
      localStorage.setItem(LANG_KEY, b.lang);
      return b.lang;
    }
    return localStorage.getItem(LANG_KEY) || (navigator.language || 'en').split('-')[0];
  } catch (_) {
    return (b && b.lang) || 'en';
  }
}

// Secondary languages the user also understands.
export function getMyAlso(tinode) {
  const b = meBabel(tinode);
  return (b && Array.isArray(b.also)) ? b.also : [];
}

// Save languages on the account (Tinode merges `public`, so name/photo are kept).
export function saveMyLanguages(tinode, lang, also) {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch (_) { /* ignore */ }
  return tinode.getMeTopic().setMeta({desc: {public: {babel: {lang: lang, also: also}}}});
}

// Does this message content need translating? Plain text, or a voice message (Drafty with an AU entity).
export function needsTranslation(content, Drafty) {
  if (typeof content == 'string') {
    return content.trim().length > 0;
  }
  let voice = false;
  try {
    Drafty.entities(content, (_0, _1, tp) => { if (tp == 'AU') voice = true; });
  } catch (_) { /* not Drafty */ }
  return voice;
}

const cache = new Map(); // "topic:seq" -> Promise of the API answer
const resolved = new Map(); // "topic:seq" -> last final answer (shown at once, without a loading flicker)

// Ask the bridge for my translation of a message from someone else (and the transcript for voice).
// Answer: {state: 'done'|'same'|'pending'|'failed'|'none', kind, lang, text, transcript, source_lang}.
// The server records that this recipient was served, which drives the sender's T indicator.
export function fetchTranslation(tinode, topic, seq, force, stage) {
  const key = topic + ':' + seq + (stage ? ':' + stage : '');
  if (!force && cache.has(key)) {
    return cache.get(key);
  }
  const token = tinode.getAuthToken();
  const p = fetch('babel/v1/translation?topic=' + encodeURIComponent(topic) + '&seq=' + seq + (stage ? '&stage=' + stage : ''), {
    headers: {'Authorization': 'Bearer ' + (token && token.token)}
  }).then(r => r.ok ? r.json() : {state: 'none', reason: 'http_' + r.status})
    .catch(_ => ({state: 'failed'}));
  cache.set(key, p);
  p.then(res => {
    if (res.state == 'pending' || res.state == 'failed') {
      cache.delete(key);
    } else if (!stage) {
      resolved.set(key, res);
    }
  });
  return p;
}

// Final answer for this message if we already have it (no network).
export function peekTranslation(topic, seq) {
  return resolved.get(topic + ':' + seq) || null;
}

// Is this a voice message (Drafty with an audio entity)?
export function isVoice(content, Drafty) {
  if (typeof content == 'string') {
    return false;
  }
  let voice = false;
  try {
    Drafty.entities(content, (_0, _1, tp) => { if (tp == 'AU') voice = true; });
  } catch (_) { /* not Drafty */ }
  return voice;
}

// Translation progress of my own messages, batched: lookups for the same topic within a short window
// become one request. Resolves to {need, served, state: 'none'|'pending'|'done'|'failed'} or null
// when the bridge does not know the message (yet).
const statusQueue = new Map(); // topic -> {timer, waiters: Map(seq -> [resolve])}

export function fetchStatus(tinode, topic, seq) {
  return new Promise(resolve => {
    let q = statusQueue.get(topic);
    if (!q) {
      q = {waiters: new Map(), timer: null};
      statusQueue.set(topic, q);
      q.timer = setTimeout(() => {
        statusQueue.delete(topic);
        const seqs = [...q.waiters.keys()];
        const token = tinode.getAuthToken();
        fetch('babel/v1/status?topic=' + encodeURIComponent(topic) + '&seqs=' + seqs.join(','), {
          headers: {'Authorization': 'Bearer ' + (token && token.token)}
        }).then(r => r.ok ? r.json() : {messages: {}})
          .catch(_ => ({messages: {}}))
          .then(res => seqs.forEach(sq => q.waiters.get(sq).forEach(done => done((res.messages || {})[sq] || null))));
      }, 80);
    }
    if (!q.waiters.has(seq)) {
      q.waiters.set(seq, []);
    }
    q.waiters.get(seq).push(resolve);
  });
}

// Sign-in: a phone number + one-time code goes to the organization's member list (Tinode "rest" authenticator,
// scheme 'rest'); anything else is a normal username + password (the sample users while we are in development).
export function looksLikePhone(login) {
  return /^\+?[\d\s\-().]{7,}$/.test((login || '').trim());
}

export function babelLogin(tinode, login, password, cred) {
  if (looksLikePhone(login)) {
    return tinode.login('rest', btoa(login.trim() + ':' + password), cred);
  }
  return tinode.loginBasic(login, password, cred);
}

// Join the organization-wide group ("Everyone") once per account and device, so a new member has people to talk to.
// The bridge names the group (GET babel/v1/org); a default access mode on the group lets any member join.
export async function babelAutoJoin(tinode) {
  try {
    const token = tinode.getAuthToken();
    const org = await fetch('babel/v1/org', {headers: {'Authorization': 'Bearer ' + (token && token.token)}})
      .then(r => r.ok ? r.json() : {});
    if (!org.group) {
      return;
    }
    const key = 'babel-org-joined:' + tinode.getCurrentUserID() + ':' + org.group;
    let have = false;
    tinode.getMeTopic().contacts(c => { if (c.topic == org.group) have = true; });
    if (have || localStorage.getItem(key)) {
      return; // already a member, or joined before and later left: do not drag them back in
    }
    const topic = tinode.getTopic(org.group);
    await topic.subscribe(topic.startMetaQuery().withLaterSub().build());
    await topic.leave(false); // joining created the membership; do not keep the chat open
    const me = tinode.getMeTopic(); // fetch the group's details (name, picture) for the contact list
    await me.getMeta(me.startMetaQuery().withLaterOneSub(org.group).build());
    localStorage.setItem(key, '1');
  } catch (_) { /* best effort: searching for colleagues still works */ }
}
